import React, { useState } from "react";
import { Alert, Grid, Grow, MenuItem, Typography } from "@mui/material";
import TranslationWrapper, { getTranslation } from "../../../../components/TranslationWrapper/TranslationWrapper";
import FormSwitchTC from "../../../../components/Forms/FormSwitchTC";
import FormControlledInputTC from "../../../../components/Forms/FormControlledInputTC";
import FormTextTC from "../../../../components/Forms/FormTextTC";
import FormFile from "../../../../components/Forms/FormFile";
import Card from "../../../../components/Card/Card";
import CardBody from "../../../../components/Card/CardBody";
import FormSelectTC from "../../../../components/Forms/FormSelectTC";
import ArticlegroupsShortcutPage from "./ArticlegroupsShortcutPage";
import BlinkstickSettings from "../BlinkstickSettings";
import Allergens from "../Allergens";
import FormTimePickerHoursMinutes from "../../../../components/Forms/FormTimePickerHoursMinutes";
import { ImageResolutionHint } from "../../../../components/ImageHints/ImageResolutionHint";
import { FilterRibbon } from "../../../../components/FilterRibbon/FilterRibbon";
import Checkbox from "@mui/material/Checkbox";
import AuthenticationWrapper from "../../../../../beheer/auth/AuthenticationWrapper";
import { ScheduledHomescreenMedia } from "../../../../components/ScheduledHomeMedia/ScheduledHomescreenMedia";
import FormJson from "../../../../components/Forms/FormJson";
import { useFormContext, useWatch } from "react-hook-form";
import FormColorTC from "../../../../components/Forms/FormColorTC";
import CustomizableArticles from "../CustomizableArticles";
import AgeCheckShoppingCartConfirmation from "./ShoppingCartConfirmations/AgeCheckShoppingCartConfirmation";
import { CustomizableTexts } from "../../../../components/CustomizableTexts/CustomizableTexts";
import { VestigingFormContext } from "../../VestigingForm";
import CustomAgeVerifyNotification from "./CustomAgeVerifyNotification";

const namedJamezzFiles = [
  { name: "kioskv5_background_of_home_page_open", res: "1080x1920" },
  { name: "kioskv5_background_of_home_page_closed", res: "1080x1920" },
  { name: "kioskv5_background_of_menu_page_content", res: "1080x1920" },
  { name: "kioskv5_background_of_menu_page_header", res: "1080x132" },
  { name: "kioskv5_background_of_order_article_selector_page", res: "1080x1920" },
  { name: "kioskv5_background_of_order_page", res: "1080x1920" },
  { name: "kioskv5_background_of_checkout_page", res: "1080x1920" },
  { name: "kioskv5_background_of_return_page", res: "1080x1920" },
  { name: "kioskv5_background_of_shopping_cart_button", res: "250x250" },
  { name: "kioskv5_logo_of_shopping_cart_button", res: "186x87" },
  { name: "kioskv5_background_of_price_label" },
];
const translatableTexts = [
  "voucher button text",
  "kiosk - Orderpage order more button",
  "kiosk - Orderpage checkout button",
  "kiosk - Orderpage order button",
  "kiosk - Orderpage goto checkout button",
];

const ribbonComponents = [
  {
    screenTitle: "All",
    screenIndex: "none",
    screenImage: "",
    screenWidth: 108,
    screenHeight: 192,
  },
  {
    screenTitle: "Start",
    screenIndex: "1",
    screenImage: "/img/customComponents/FilterRibbon_Intro_001.png",
    screenDescription: "Start screen on an idle kiosk",
    screenWidth: 108,
    screenHeight: 192,
  },
  {
    screenTitle: "Categories",
    screenIndex: "2",
    screenImage: "/img/customComponents/FilterRibbon_Category_002.png",
    screenDescription: "Categories and products screen",
    screenWidth: 108,
    screenHeight: 192,
    subScreenInfo: [
      {
        screenTitle: "Categories",
        screenIndex: "2A",
        screenImage: "",
        screenDescription: "Top or side category selection",
        screenWidth: 108,
        screenHeight: 192,
        areaWidth: 108,
        areaHeight: 30,
        areaPositionY: 8,
        areaPositionX: 0,
      },
      {
        screenTitle: "Products",
        screenIndex: "2B",
        screenImage: "",
        screenDescription: "Displayed products of selected category",
        screenWidth: 108,
        screenHeight: 192,
        areaWidth: 108,
        areaHeight: 85,
        areaPositionY: 78,
        areaPositionX: 0,
      },
      {
        screenTitle: "Cart button",
        screenIndex: "2C",
        screenImage: "",
        screenDescription: "Button to navigate to shopping cart screen",
        screenWidth: 108,
        screenHeight: 192,
        areaWidth: 45,
        areaHeight: 23,
        areaPositionY: 170,
        areaPositionX: 32,
      },
    ],
  },
  {
    screenTitle: "Article",
    screenIndex: "3",
    screenImage: "/img/customComponents/FilterRibbon_Product_002.png",
    screenDescription: "Article detail page - add to shopping cart",
    screenWidth: 108,
    screenHeight: 192,
  },
  {
    screenTitle: "Cart",
    screenIndex: "4",
    screenImage: "/img/customComponents/FilterRibbon_Cart_001-FullHD.png",
    screenDescription: "List of total amount of ordered products and prices",
    screenWidth: 108,
    screenHeight: 192,
  },
  {
    screenTitle: "Pay",
    screenIndex: "5",
    screenImage: "/img/customComponents/FilterRibbon_Pay_003-FullHD.png",
    screenDescription: "The final payment screen",
    screenWidth: 108,
    screenHeight: 192,
  },
  {
    screenTitle: "Return",
    screenIndex: "6",
    screenImage: "/img/customComponents/FilterRibbon_Return_001-FullHD.png",
    screenDescription: "Return screen after payment",
    screenWidth: 108,
    screenHeight: 192,
  },
];

export default function KioskV5() {
  const { vestigingId }: VestigingFormContext = useFormContext();
  const blinkStickEnabled = (useWatch({ name: "extra_settings__blinkstick_settings.accessToken" }) ?? "").length > 0;
  const [selectedGroupVal, setSelectedGroupVal] = useState("none");
  const [showAdminValues, setShowAdminValues] = useState(false);
  const confirmAgeByEmployeeEnabled = useWatch({ name: "custom_data.kioskv5.confirm_age_by_employee" });

  const toggleAdminMode = () => {
    setShowAdminValues(!showAdminValues);
  };

  const changeSelector = (newSelector: any) => {
    setSelectedGroupVal(newSelector);
  };

  const translatedAdminEditMode = getTranslation("KioskV5_SortingRibbon.Admin_edit_mode", "not found");

  const listOfInactiveAreas = translatedAdminEditMode.slice(
    translatedAdminEditMode.indexOf("["),
    translatedAdminEditMode.lastIndexOf("]") + 1
  );

  const translatedAdminEditModeText = translatedAdminEditMode.replace(listOfInactiveAreas, "");
  const mediaUrls = useWatch({ name: "media_urls" });

  return (
    <div style={{ height: "100%", width: "100%" }}>
      <Card>
        <CardBody alignItems={"flex-start"} style={{ padding: 16, display: "flex", flexWrap: "wrap" }}>
          <Grid container spacing={1} style={{ maxWidth: 1400 }}>
            <Grid item xs={12}>
              <h3>
                <TranslationWrapper tDefault={"Limieten"} tKey={"vestigingen.form.algemeen.headers.limieten"} />
              </h3>
            </Grid>
            <FormSwitchTC
              name={"orderLimits"}
              tDefault={"Order limieten gebruiken"}
              tKey={"vestigingen.form.overige.label.orderLimits"}
            />
            <FormControlledInputTC
              name={"minOrderValue"}
              tDefault={"Min order value"}
              tKey={"vestigingen.form.overige.label.minOrderValue"}
              type={"currency"}
            />
            <FormControlledInputTC
              name={"maxOrderValue"}
              tDefault={"Max order value"}
              tKey={"vestigingen.form.overige.label.maxOrderValue"}
              type={"currency"}
            />
          </Grid>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h3>
            <TranslationWrapper tDefault={"Various kiosk settings"} tKey={"kiosk_various_settings_header"} />
          </h3>
          <Grid container spacing={1} style={{ maxWidth: 1400 }}>
            <FormTextTC name={"start_url"} tDefault={"Home Page Url (e.g. mastercode)"} tKey={"vestiging.start_url"} />
            <FormSwitchTC
              name={"extra_settings__voucherShowKeyboard"}
              tDefault={"Show Voucher-Keyboard"}
              tKey={"Show Voucher-Keyboard"}
            />
            <FormSwitchTC
              name={"custom_data.kiosk.show_wheelchair_friendly_button"}
              tDefault={"Show Wheelchair-friendly button"}
              tKey={"Show Wheelchair-friendly button"}
            />
            <FormSwitchTC
              defaultValue={true}
              name={"custom_data.upsell_enabled"}
              tDefault={"Upsell functionality enabled"}
              tKey={"Upsell functionality enabled"}
            />

            <FormSelectTC
              defaultValue={"QWERTY"}
              name={"custom_data.keyboard_layout"}
              options={[
                <MenuItem key={"qwerty"} value={"QWERTY"}>
                  <TranslationWrapper tDefault={"Qwerty"} tKey={"custom_data.keyboard_layout.QWERTY"} />
                </MenuItem>,
                <MenuItem key={"azerty"} value={"AZERTY"}>
                  <TranslationWrapper tDefault={"Azerty"} tKey={"custom_data.keyboard_layout.AZERTY"} />
                </MenuItem>,
              ]}
              tDefault={"Keyboard layout (Qwerty / Azerty)"}
              tKey={"extra_settings__custom_data.keyboard_layout"}
            />
            <FormSelectTC
              defaultValue={"flags"}
              name={"custom_data.kiosk.how_to_present_languages"}
              options={[
                <MenuItem key={"flags"} value={"flags"}>
                  <TranslationWrapper
                    tDefault={"Using flags"}
                    tKey={"custom_data.kiosk.how_to_present_languages.flags"}
                  />
                </MenuItem>,
                <MenuItem key={"text"} value={"text"}>
                  <TranslationWrapper
                    tDefault={"Using text"}
                    tKey={"custom_data.kiosk.how_to_present_languages.text"}
                  />
                </MenuItem>,
              ]}
              tDefault={"How to present languages in kiosk"}
              tKey={"custom_data.kiosk.how_to_present_languages"}
            />

            <FormSwitchTC
              defaultValue={true}
              name={"custom_data.kiosk.enable_product_barcode_scanner"}
              tDefault={"Product barcode scanner enabled"}
              tKey={"custom_data.kiosk.enable_product_barcode_scanner"}
            />

            <FormSwitchTC
              name={"extra_settings__compact_options_in_kiosk_order_page"}
              tDefault={"Collapsed options in order page"}
              tKey={"vestigingen.form.zuil.layout.collapsedOptions"}
            />
            <FormSwitchTC
              name={"printWithAPI"}
              tDefault={"Printbon"}
              tKey={"vestigingen.form.zuil.label.printWithAPI"}
            />
            <FormSwitchTC name={"printVat"} tDefault={"Print btw"} tKey={"vestigingen.form.zuil.label.printVat"} />
            <FormTextTC
              name={"instoreAfterOrderUrl"}
              tDefault={"Url after order"}
              tKey={"vestigingen.form.zuil.label.instoreAfterOrderUrl"}
            />
            <FormControlledInputTC
              name={"custom_data.kiosk.zoom_factor"}
              nullable
              rules={{
                min: {
                  value: 0,
                  message: <TranslationWrapper tKey={"The value must not be negative!"} />,
                },
              }}
              tDefault={"Zoom Factor"}
              tKey={"Zoom Factor"}
              type={"number"}
            />
            <FormControlledInputTC
              name={"afterOrderTimeout"}
              rules={{
                min: {
                  value: 0,
                  message: <TranslationWrapper tKey={"The value must not be negative!"} />,
                },
              }}
              tDefault={"Url after order timeout"}
              tKey={"vestigingen.form.zuil.label.afterOrderTimeout"}
              type={"number"}
            />

            <FormControlledInputTC
              name={"refreshTimeout"}
              rules={{
                min: {
                  value: 0,
                  message: <TranslationWrapper tKey={"The value must not be negative!"} />,
                },
                max: {
                  value: 86400,
                  message: <TranslationWrapper tKey={"The value may be maximal 86400!"} />,
                },
              }}
              tDefault={"Logout after number of seconds"}
              tKey={"vestigingen.form.zuil.label.refreshTimeout"}
              type={"number"}
            />
            <FormTimePickerHoursMinutes
              name={"extra_settings__kioskRestartTime"}
              tDefault={"Reload kiosk at"}
              tKey={"vestigingen.form.zuil.label.kioskRestartTime"}
            />

            <FormSelectTC
              name={"extra_settings__orientation_of_categories"}
              options={[
                <MenuItem value={"VERTICAL"}>
                  <TranslationWrapper
                    tDefault={"Vertical"}
                    tKey={"extra_settings__orientation_of_categories.VERTICAL"}
                  />
                </MenuItem>,
                <MenuItem value={"HORIZONTAL"}>
                  <TranslationWrapper
                    tDefault={"Horizontal"}
                    tKey={"extra_settings__orientation_of_categories.HORIZONTAL"}
                  />
                </MenuItem>,
                <MenuItem value={"MULTIPLE_LAYERS"}>
                  <TranslationWrapper
                    tDefault={"Multiple Layers Horizontal"}
                    tKey={"extra_settings__orientation_of_categories.MULTIPLE_LAYERS"}
                  />
                </MenuItem>,
              ]}
              tDefault={"Catalog View"}
              tKey={"extra_settings__orientation_of_categories"}
            />
            <FormSelectTC
              defaultValue={"swiper"}
              name={"custom_data.kiosk.order_article_page_selector_style"}
              options={[
                <MenuItem value={"swiper"}>
                  <TranslationWrapper
                    tDefault={"Swiper (Horizontal Scroll)"}
                    tKey={"custom_data.kiosk.order_article_page_selector_style.swiper"}
                  />
                </MenuItem>,
                <MenuItem value={"vertical_scrolling"}>
                  <TranslationWrapper
                    tDefault={"List (Vertical scroll) experimental"}
                    tKey={"custom_data.kiosk.order_article_page_selector_style.vertical_scrolling"}
                  />
                </MenuItem>,
              ]}
              tDefault={"Order Article Configurator Style"}
              tKey={"custom_data.kiosk.order_article_page_selector_style"}
            />
            <FormSelectTC
              name={"extra_settings__type_of_shopping_cart_button"}
              options={[
                <MenuItem value={"WIDE"}>
                  <TranslationWrapper tDefault={"Wide"} tKey={"extra_settings__type_of_shopping_cart_button.WIDE"} />
                </MenuItem>,
                <MenuItem value={"SMALL"}>
                  <TranslationWrapper tDefault={"Small"} tKey={"extra_settings__type_of_shopping_cart_button.SMALL"} />
                </MenuItem>,
              ]}
              tDefault={"Type of shopping cart button"}
              tKey={"extra_settings__type_of_shopping_cart_button"}
            />

            <FormTextTC
              name={"extra_settings__customKioskButtonsEatin"}
              rows={5}
              tDefault={"Custom kiosk button eat in"}
              tKey={"vestigingen.form.algemeen.label.customKioskButtonsEatin"}
            />
            <FormTextTC
              name={"extra_settings__customKioskButtonsTakeaway"}
              rows={5}
              tDefault={"Custom kiosk button takeaway"}
              tKey={"vestigingen.form.algemeen.label.customKioskButtonsTakeaway"}
            />

            <FormSwitchTC
              name={"custom_data.kiosk.selected_articlegroup_name_in_kiosk_url"}
              tDefault={"Add name of the selected articlegroup to kiosk url"}
              tKey={"Kiosk.selected_articlegroup_name_in_kiosk_url"}
            />

            <FormSwitchTC
              name={"extra_settings__schiphol_boarding_pass"}
              tDefault={"Schiphol boarding pass scanner"}
              tKey={"vestigingen.form.kiosk.schiphol_boarding_pass"}
            />
            <FormSwitchTC
              name={"custom_data.kioskv5.show_warning_options_belong_to_every_unit"}
              tDefault={"Show warning: Options belong to every unit of an item"}
              tKey={"estigingen.form.kiosk.show_warning_options_belong_to_every_unit"}
            />
            <FormSwitchTC name={"use_packagings"} tDefault={"Use Packagings"} tKey={"use_packagings"} />
            <FormSwitchTC
              defaultValue={true}
              name={"custom_data.scanner_hw_available"}
              tDefault={"Kiosk has hardware scanner"}
              tKey={"vestigingen.scanner_hw_available"}
            />

            <FormControlledInputTC
              name={"custom_data.kiosk.max_alcoholic_items_in_basket"}
              tDefault={"Max alcoholic items per order. (Empty is unlimited)"}
              tKey={"custom_data.kiosk.max_alcoholic_items_in_basket"}
            />

            <FormSelectTC
              defaultValue={"8"}
              name={"custom_data.kioskv5.compact_layout_from_x_number_of_options"}
              options={[
                <MenuItem value={"8"}>8</MenuItem>,
                <MenuItem value={"12"}>12</MenuItem>,
                <MenuItem value={"16"}>16</MenuItem>,
              ]}
              tDefault={"Auto change to compact layout: above x of options (Article Selector Page)"}
              tKey={"custom_data.kiosk.compact_layout_from_x_number_of_options"}
            />
          </Grid>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h3>
            <TranslationWrapper tDefault={"Other article settings"} tKey={"vestigingen.form.kiosk.otherarticles"} />
          </h3>
          <CustomizableArticles />
          <FormSwitchTC
            defaultValue={false}
            name={"custom_data.disable_user_input_no_ean"}
            tKey={"Disable login when ean code is not found"}
          />
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h3>
            <TranslationWrapper tDefault={"Home screen settings"} tKey={"vestigingen.form.kiosk.heading.homeScreen"} />
          </h3>
          <ScheduledHomescreenMedia />
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <Grid container>
            <Grid item md={6} xs={12}>
              <h3>
                <TranslationWrapper tDefault={"Kiosk images"} tKey={"Kiosk_images"} />
              </h3>
            </Grid>

            <AuthenticationWrapper
              child={(write) => (
                <Grid
                  item
                  md={6}
                  style={{ marginBottom: 10, marginTop: 15, display: "flex", alignItems: "center" }}
                  xs={12}
                >
                  <Checkbox
                    checked={showAdminValues}
                    color={"primary"}
                    onChange={toggleAdminMode}
                    size={"small"}
                    style={{ width: 50, height: 50 }}
                  />

                  {showAdminValues || listOfInactiveAreas === "" ? (
                    <TranslationWrapper tDefault={"Admin edit mode"} tKey={"QrV5_SortingRibbon.Admin_edit_mode"} />
                  ) : (
                    <Typography>{translatedAdminEditModeText}</Typography>
                  )}
                </Grid>
              )}
              permissionKey={"brandingdesign.customcomponents.admineditmode"}
            />
          </Grid>

          <Grid item xs={12}>
            <FilterRibbon
              allSelectedFilterText={"Show all images"}
              areaHighlightBackground={"#000000"}
              areaHighlightBackgroundOpacity={175}
              areaOpacity={100}
              changeSelector={changeSelector} //** Important
              colorGroup1Background={"#f46e26"}
              colorGroup1Foreground={"#ffffff"}
              colorGroup2Background={"#ffffff"}
              colorGroup2Foreground={"#000000"}
              colorGroup3Background={"#f46e26"}
              colorGroup3Foreground={"#ffffff"}
              inactiveAreaColor={"#FF0000"}
              inactiveAreas={listOfInactiveAreas}
              ribbonComponents={ribbonComponents} //** Important
              selectedGroupVal={selectedGroupVal} //** Important
              showAdminInfo={showAdminValues}
              showBreadcrumbs={false}
              showFilterReturnButton={true}
              showHoverDescription={true}
              shownDescription={"none"} // options: side, bottom (default), none
            />
          </Grid>

          <Grid container>
            {namedJamezzFiles.map((namedJamezzFile) => {
              const translatedImageTitle = getTranslation(`vestigingen.form.zuil.label.${namedJamezzFile.name}`);

              const screenIndex = translatedImageTitle.slice(
                translatedImageTitle.indexOf("["),
                translatedImageTitle.lastIndexOf("]") + 1
              );

              const errorColor =
                showAdminValues &&
                (translatedImageTitle === "notfound" ||
                  (!translatedImageTitle.includes("[") && !translatedImageTitle.includes("[")))
                  ? { color: "red" }
                  : {};

              if (screenIndex.includes(selectedGroupVal) || selectedGroupVal === "none") {
                return (
                  <Grid item key={namedJamezzFile.name} style={errorColor} xs={4}>
                    {showAdminValues ? (
                      <TranslationWrapper
                        tDefault={namedJamezzFile.name}
                        tKey={`vestigingen.form.zuil.label.${namedJamezzFile.name}`}
                      />
                    ) : (
                      <p style={{ margin: 0 }}>{namedJamezzFile.name}</p>
                    )}
                    {namedJamezzFile.res ? <ImageResolutionHint res={namedJamezzFile.res} /> : null}
                    <FormFile
                      canCrop={false}
                      imgurl={mediaUrls?.[`extra_settings__${namedJamezzFile.name}`]?.[0]?.conversions?.optimized?.url}
                      name={`extra_settings__${namedJamezzFile.name}`}
                      nullable={true}
                      originalUrl={mediaUrls?.[`extra_settings__${namedJamezzFile.name}`]?.[0]?.original}
                      type={"file"}
                    />
                  </Grid>
                );
              }
            })}
          </Grid>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h3>
            <TranslationWrapper
              tDefault={"Age check settings"}
              tKey={"vestigingen.form.algemeen.qrv5.header.age_check_settings"}
            />
          </h3>
          <Grow in={!blinkStickEnabled ? confirmAgeByEmployeeEnabled : null} mountOnEnter unmountOnExit>
            <Alert severity={"info"}>
              <TranslationWrapper
                tDefault={
                  "It is recommended to use the Age Check feature in combination with a blinkstick. The blinkstick will light up when a customer needs their age verified."
                }
                tKey={"confirm_age_by_employee.warning.blinkstickRecommended"}
              />
            </Alert>
          </Grow>
          <Grid container>
            <FormSwitchTC
              name={"custom_data.kioskv5.confirm_age_by_employee"}
              tDefault={"Age-restricted products must be confirmed by an employee"}
              tKey={"vestigingen.form.algemeen.headers.qrv5.verify_age_by_employee"}
            />
            <Grow in={confirmAgeByEmployeeEnabled} mountOnEnter unmountOnExit>
              <div>
                {blinkStickEnabled ? (
                  <FormColorTC
                    name={"extra_settings__blinkstick_settings.checkAgeColor"}
                    rules={{ required: true }}
                    tDefault={"Blinkstick age check color"}
                    tKey={"blinkstick_settings.checkAgeColor"}
                  />
                ) : null}
                <hr style={{ width: "100%" }} />
                <Typography style={{ marginBottom: "16px" }} variant={"body2"}>
                  An employee has to fill in a code after verifying a customer&apos;s age. When an order is placed, the
                  code used to verify that order will be present in the order. Because of this reason, it is recommended
                  to give each customer their own unique pin code.
                </Typography>
                <FormJson
                  name={"custom_data.employee_codes"}
                  rules={{ required: true }}
                  tDefault={"Employee codes"}
                  tKey={"Employee codes"}
                />
              </div>
            </Grow>
          </Grid>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h3>
            <TranslationWrapper tDefault={"Custom texts"} tKey={"vestigingen.form.algemeen.headers.kiosk.text"} />
          </h3>
          <h4>
            <TranslationWrapper
              tDefault={"Order and Checkout"}
              tKey={"vestigingen.form.algemeen.headers.kiosk.custom_texts.order_and_checkout"}
            />
          </h4>
          <CustomizableTexts translatableTexts={translatableTexts} vestigingId={vestigingId} />
        </CardBody>
      </Card>

      <AgeCheckShoppingCartConfirmation />
      <CustomAgeVerifyNotification />
      <ArticlegroupsShortcutPage />

      <BlinkstickSettings />

      <Allergens />
    </div>
  );
}
